import { useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";

function RecordDetails({ record }) {
  return <span className="break-all font-mono">
    {record.Type || record.type} · {record.Name || record.host} · {record.Address || record.value}
  </span>;
}

export default function WebsiteDnsPlan({ hostname, locked, run }) {
  const [plan, setPlan] = useState(null);
  const [notice, setNotice] = useState("");
  const load = () => run("dns-plan", async () => {
    const { data } = await axios.get(
      API + "/website-builder/publications/hosting/" + encodeURIComponent(hostname) + "/dns-plan",
    );
    setPlan(data); setNotice("");
  });

  const apply = () => {
    if (locked || !plan || plan.blocked || !plan.apply_available || !plan.proposed_additions.length) return;
    if (!window.confirm("Apply the displayed website DNS additions for " + plan.domain +
      "? Review the existing records and confirmed email-routing settings first. DNS changes can take time to propagate.")) return;
    const proposal = plan;
    return run("dns-apply", async () => {
      setPlan(null); setNotice("");
      const { data } = await axios.post(
        API + "/website-builder/publications/hosting/" + encodeURIComponent(hostname) + "/dns-apply",
        { snapshot_sha256: proposal.snapshot_sha256, confirm_dns_change: true },
      );
      setNotice(data.dns_changed
        ? "DNS additions confirmed at the registrar. Hosting still needs DNS propagation and HTTPS checks. Nothing has been published."
        : "The requested website records were already present. No DNS changes were needed.");
    });
  };

  return (
    <section className="space-y-3 border-t pt-3" aria-label="Website DNS proposal">
      <button type="button" disabled={locked} onClick={load}
        className="rounded border px-3 py-2 text-sm disabled:opacity-50">
        {plan ? "Refresh DNS proposal" : "Read records and prepare DNS proposal"}
      </button>
      <p className="text-xs text-slate-500">This action reads current registrar DNS and compares website records. It does not apply changes.</p>
      {notice && <p role="status" className="text-sm text-emerald-700">{notice}</p>}
      {plan && <div className="space-y-3">
        <p className="text-sm font-medium">DNS proposal for {plan.domain}</p>
        {!plan.using_registrar_dns && <p role="alert" className="text-sm text-amber-700">
          This domain uses external DNS. Registrar records may not represent its active DNS zone. Manage the website records at the active DNS provider; nameservers have not been changed.
        </p>}
        {plan.conflicts.length > 0 && <div className="space-y-2">
          <p role="alert" className="text-sm text-amber-700">Existing records conflict with the website setup. Review them before any future change.</p>
          <ul className="space-y-2">
            {plan.conflicts.map((conflict, index) => <li key={index} className="space-y-1 rounded border p-2 text-xs">
              <p>Existing: <RecordDetails record={conflict.existing} /></p>
              <p>Requested: <RecordDetails record={conflict.requested} /></p>
            </li>)}
          </ul>
        </div>}
        <div className="space-y-2">
          <h5 className="text-xs font-semibold">Proposed additions</h5>
          {plan.proposed_additions.length === 0
            ? <p className="text-xs text-slate-500">The requested records are already present in this snapshot.</p>
            : <ul className="space-y-1 text-xs">{plan.proposed_additions.map((record, index) =>
              <li key={index} className="rounded border p-2"><RecordDetails record={record} /></li>)}</ul>}
        </div>
        <details className="rounded border p-2">
          <summary className="cursor-pointer text-xs font-medium">View existing registrar records ({plan.original_records.length})</summary>
          <div className="mt-2 max-h-80 overflow-auto">
            <table className="w-full text-left text-xs">
              <thead><tr><th className="p-2">Type</th><th className="p-2">Host</th><th className="p-2">Value</th><th className="p-2">TTL</th></tr></thead>
              <tbody>{plan.original_records.map((record, index) => <tr key={record.HostId || index} className="border-t">
                <td className="p-2">{record.Type}</td><td className="p-2">{record.Name}</td>
                <td className="max-w-xs break-all p-2 font-mono">{record.Address}</td><td className="p-2">{record.TTL}</td>
              </tr>)}</tbody>
            </table>
          </div>
        </details>
        <p className="text-xs text-slate-500">Snapshot only. DNS can change after it is read. Applying additions requires confirmed email routing, an enabled DNS connection and your confirmation. Existing conflicts need review first.</p>
        {!plan.apply_available && <p className="text-xs text-amber-700">DNS application is disabled or existing email-routing settings still need confirmation.</p>}
        <button type="button" disabled={locked || plan.blocked || !plan.apply_available || !plan.proposed_additions.length}
          onClick={apply} className="rounded border px-3 py-2 text-sm disabled:opacity-50">
          Review and apply DNS additions
        </button>
      </div>}
    </section>
  );
}
